import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Heart, ShieldCheck, CheckCircle2, ArrowRight, Phone, Calculator, HandHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useQuote } from "@/context/QuoteContext";
import { SITE, IMAGES } from "@/data/site";

const PERKS = [
  { title: "Up to $250,000", desc: "Financing available for projects big and small." },
  { title: "Challenged Credit? Still Apply", desc: "Less-than-perfect credit history is welcome here." },
  { title: "Soft Credit Check", desc: "Checking your options won't hurt your credit score." },
  { title: "Fast, Friendly Decisions", desc: "Quick answers and affordable monthly payments." },
];

const TERMS = [
  { months: 60, label: "5 yrs" },
  { months: 120, label: "10 yrs" },
  { months: 180, label: "15 yrs" },
  { months: 240, label: "20 yrs" },
];
const APR = 0.0999;

const PaymentEstimator = () => {
  const { openQuote } = useQuote();
  const [amount, setAmount] = useState(25000);
  const [months, setMonths] = useState(120);

  const monthly = useMemo(() => {
    const r = APR / 12;
    const m = (amount * r) / (1 - Math.pow(1 + r, -months));
    return Math.round(m);
  }, [amount, months]);

  return (
    <div data-testid="payment-estimator" className="rounded-2xl bg-slate-950 text-white p-6 sm:p-8 shadow-xl">
      <div className="flex items-center gap-2 text-red-500">
        <Calculator className="h-5 w-5" />
        <span className="text-sm uppercase tracking-[0.15em] font-bold">Estimate My Monthly Payment</span>
      </div>

      <div className="mt-6">
        <div className="flex items-baseline justify-between">
          <label className="text-sm font-semibold text-slate-300">Project amount</label>
          <span data-testid="estimator-amount" className="font-display font-extrabold text-2xl text-white">
            ${amount.toLocaleString()}
          </span>
        </div>
        <Slider
          data-testid="estimator-slider"
          value={[amount]}
          min={2500}
          max={250000}
          step={2500}
          onValueChange={(v) => setAmount(v[0])}
          className="mt-4"
        />
        <div className="flex justify-between text-xs text-slate-500 mt-2">
          <span>$2,500</span>
          <span>$250,000</span>
        </div>
      </div>

      <div className="mt-6">
        <label className="text-sm font-semibold text-slate-300">Term length</label>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {TERMS.map((t) => (
            <button
              key={t.months}
              data-testid={`estimator-term-${t.months}`}
              onClick={() => setMonths(t.months)}
              className={`rounded-lg py-2.5 text-sm font-bold transition-colors ${
                months === t.months ? "bg-red-700 text-white" : "bg-white/10 text-slate-300 hover:bg-white/20"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-7 rounded-xl bg-white/5 border border-white/10 p-5 text-center">
        <p className="text-sm text-slate-400 font-medium">Estimated monthly payment</p>
        <p data-testid="estimator-monthly" className="font-display font-extrabold text-4xl text-red-500 mt-1">
          ${monthly.toLocaleString()}<span className="text-lg text-slate-400 font-semibold">/mo</span>
        </p>
        <p className="text-xs text-slate-500 mt-2">Based on 9.99% average market APR. You may qualify for 0% financing. Estimate only, actual terms may vary by approval.</p>
      </div>

      <Button
        data-testid="estimator-apply-btn"
        onClick={() => openQuote("Financing / Payment Plan")}
        className="mt-5 w-full bg-red-700 hover:bg-red-800 text-white font-bold rounded-full h-12 hover:scale-[1.02] transition-transform"
      >
        See If You Qualify <ArrowRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
};

export const Financing = () => {
  const { openQuote } = useQuote();

  return (
    <section id="financing" data-testid="financing-section" className="py-16 sm:py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] font-bold text-red-700">
              <Heart className="h-4 w-4" /> Financing Made Human
            </span>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-950 text-balance">
              Your Family's Safety Shouldn't Wait for Perfect Credit
            </h2>

            <div className="mt-5 flex items-start gap-3 rounded-xl bg-red-700 text-white p-4">
              <HandHeart className="h-6 w-6 shrink-0 mt-0.5" />
              <p className="font-display font-semibold text-lg leading-snug">
                We're not here to sell you, we're here to <span className="underline decoration-2 underline-offset-2">serve</span> you.
              </p>
            </div>

            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Life happens, and we truly get it. For <span className="font-semibold text-slate-800">46 years</span>,
              Templar Armor has protected California families through every season of life. We believe everyone
              deserves a safe, beautiful home over their head.
            </p>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              That's why we help homeowners qualify for <span className="font-semibold text-red-700">up to $250,000 in financing,</span> even with a challenged credit history. No shame, no pressure. Just a real chance to protect what matters most.
            </p>

            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              {PERKS.map((p) => (
                <div key={p.title} className="flex gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <CheckCircle2 className="h-5 w-5 text-red-700 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display font-bold text-slate-900">{p.title}</div>
                    <div className="text-sm text-slate-600">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-7 rounded-xl bg-red-50 border border-red-100 p-4 flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-red-700 shrink-0" />
              <p className="text-slate-700 font-medium">
                It only takes a minute to see your options, and honestly, <span className="font-bold text-red-700">it's worth a try.</span>
              </p>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="financing-apply-btn"
                onClick={() => openQuote("Financing / Payment Plan")}
                size="lg"
                className="bg-red-700 hover:bg-red-800 text-white font-bold rounded-full h-14 px-8 hover:scale-105 transition-transform shadow-lg shadow-red-900/20"
              >
                See If You Qualify <ArrowRight className="ml-1 h-5 w-5" />
              </Button>
              <a href={SITE.phoneHref} data-testid="financing-call-btn">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-slate-300 text-slate-900 hover:bg-slate-900 hover:text-white font-bold rounded-full h-14 px-8">
                  <Phone className="mr-1 h-5 w-5" /> Talk to Us
                </Button>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img src={IMAGES.courtyard} alt="Courtyard remodel completed by Templar Armor" className="w-full h-[220px] sm:h-[300px] object-cover" />
              </div>
              <div className="absolute -bottom-5 left-4 bg-slate-950 text-white rounded-2xl p-4 shadow-xl">
                <div className="font-display font-extrabold text-2xl text-red-600">$250K</div>
                <p className="text-xs text-slate-300 font-medium">available, even with challenged credit.</p>
              </div>
            </div>
            <PaymentEstimator />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
